import React from 'react';
import { ChevronLeft, ChevronRight, KeyRound } from 'lucide-react';
import { BRAND, TOKENS, RADIUS, headingFont, FONT_BODY } from '../lib/theme.js';

// ─── HERO CAROUSEL — BRAND.md §8.4 rebuild ───────────────────────────────────
// Images always keep a layout box (opacity stacking), the first slide loads
// eagerly, failures degrade to a branded panel, and the carousel pauses on
// hover/focus with accessible, RTL-aware controls.

const ADVANCE_MS = 5000;

function BrandPanel({ compact }) {
  // Fallback when a slide has no usable image: forest panel with the wordmark.
  return (
    <div aria-hidden="true" style={{
      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 6,
      background: `linear-gradient(150deg, ${BRAND.forest}, ${TOKENS.forestSoft})`,
    }}>
      {/* Cream, not sage: sage dropped to ~2.8:1 when forest lightened to #33402B */}
      <span style={{
        color: BRAND.cream, fontWeight: 700, letterSpacing: '0.18em',
        fontSize: compact ? 22 : 34, fontFamily: "'DM Sans', sans-serif",
      }}>SEDRAHOME</span>
      <span style={{ color: BRAND.cream, opacity: 0.85, fontSize: compact ? 13 : 16 }}>سدرة هوم</span>
    </div>
  );
}

export default function HeroBanner({ slides, lang, isMobile, type, onApply }) {
  const [current, setCurrent] = React.useState(0);
  const [failed, setFailed] = React.useState({});
  const [paused, setPaused] = React.useState(false);
  const ar = lang === 'ar';
  const count = slides.length;

  // Bandwidth guard: every slide sits inside the hero, which is above the fold,
  // so loading="lazy" never defers them — the browser fetches all N up front.
  // We instead mount an <img> only for slides the viewer has reached, plus the
  // next one so the crossfade never shows an empty frame. A visitor who never
  // waits out the carousel downloads two slides instead of all of them.
  const [mounted, setMounted] = React.useState(() => new Set([0, count > 1 ? 1 : 0]));
  React.useEffect(() => {
    if (count < 1) return;                    // no banners configured yet
    setMounted(prev => {
      const next = (current + 1) % count;
      if (prev.has(current) && prev.has(next)) return prev;   // no-op, keep identity
      const merged = new Set(prev);
      merged.add(current);
      merged.add(next);
      return merged;
    });
  }, [current, count]);

  React.useEffect(() => {
    if (count < 2 || paused) return;
    const id = setInterval(() => setCurrent(c => (c + 1) % count), ADVANCE_MS);
    return () => clearInterval(id);
  }, [count, paused]);

  const go = (delta) => setCurrent(c => (c + delta + count) % count);
  const slide = slides[current] || {};

  const arrowStyle = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)', zIndex: 3,
    width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(0,42,23,0.45)', color: '#fff',
  };

  // Physical-side arrows: the left control always shows a left chevron and the
  // right control a right chevron (glyphs point OUTWARD, never reversed). Only
  // the navigation direction is RTL-aware: in RTL content flows right→left, so
  // the left arrow advances (next) and the right arrow goes back (previous).
  const leftDelta  = ar ? 1 : -1;
  const rightDelta = ar ? -1 : 1;
  const leftLabel  = ar ? 'الشريحة التالية' : 'Previous slide';
  const rightLabel = ar ? 'الشريحة السابقة' : 'Next slide';

  return (
    <section
      dir={ar ? 'rtl' : 'ltr'}
      aria-roledescription="carousel"
      aria-label={ar ? 'عروض سدرة هوم' : 'Sedra Home highlights'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
      style={{
        position: 'relative', overflow: 'hidden', background: BRAND.forest,
        width: '100%',
        aspectRatio: isMobile ? '4 / 3' : '21 / 9',
        minHeight: isMobile ? 280 : 300, maxHeight: isMobile ? 400 : 430,
      }}
    >
      {slides.map((s, i) => (
        <div key={i} aria-hidden={i !== current} style={{
          position: 'absolute', inset: 0,
          opacity: i === current ? 1 : 0,
          transition: 'opacity 0.9s ease',
          animation: i === current ? 'sedra-kenBurns 5s ease-out forwards' : 'none',
        }}>
          {s.src && !failed[i] && mounted.has(i) ? (
            <img
              src={s.src}
              alt=""
              // Only slide 0 blocks first paint; the rest are fetched at low
              // priority once mounted, so they never compete with the catalogue.
              loading="eager"
              fetchpriority={i === 0 ? 'high' : 'low'}
              decoding="async"
              onError={() => setFailed(f => ({ ...f, [i]: true }))}
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
            />
          ) : (
            <BrandPanel compact={isMobile} />
          )}
        </div>
      ))}

      {/* Forest-tinted vignette so the copy stays readable over any image */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,42,23,0.88) 0%, rgba(0,42,23,0.32) 52%, rgba(0,42,23,0.08) 78%, rgba(0,42,23,0.20) 100%)' }} />

      {/* Progress bar */}
      {count > 1 && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: 'rgba(255,255,255,0.15)', zIndex: 2 }}>
          <div key={`${current}-${paused}`} style={{
            height: '100%', background: BRAND.tan,
            animation: `sedra-progressBar ${ADVANCE_MS}ms linear forwards`,
            animationPlayState: paused ? 'paused' : 'running',
          }} />
        </div>
      )}

      {/* Prev / next — physical sides, outward glyphs, RTL-aware navigation */}
      {count > 1 && (
        <>
          <button type="button" onClick={() => go(leftDelta)}
            aria-label={leftLabel}
            style={{ ...arrowStyle, left: 12 }}>
            <ChevronLeft size={22} aria-hidden="true" />
          </button>
          <button type="button" onClick={() => go(rightDelta)}
            aria-label={rightLabel}
            style={{ ...arrowStyle, right: 12 }}>
            <ChevronRight size={22} aria-hidden="true" />
          </button>
        </>
      )}

      {/* Copy + CTA */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 2, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'flex-end', textAlign: 'center',
        padding: isMobile ? '20px 16px 28px' : '32px 28px 44px', pointerEvents: 'none',
      }}>
        <div key={`tag-${current}`} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, animation: 'sedra-slideIn 0.5s ease both' }}>
          <span style={{ width: isMobile ? 22 : 34, height: 1, background: `linear-gradient(to right, transparent, ${BRAND.sage})` }} />
          <span style={{ fontSize: isMobile ? 10 : 11, letterSpacing: ar ? 0 : '0.3em', textTransform: ar ? 'none' : 'uppercase', color: BRAND.cream, fontWeight: 600, fontFamily: FONT_BODY }}>
            {ar ? 'كل ما يخص المنزل · جملة' : 'Everything Home · Wholesale'}
          </span>
          <span style={{ width: isMobile ? 22 : 34, height: 1, background: `linear-gradient(to left, transparent, ${BRAND.sage})` }} />
        </div>
        <h1 key={`h1-${current}`} style={{
          margin: '0 0 10px', fontSize: isMobile ? 30 : 48, fontWeight: 700, color: '#fff',
          lineHeight: ar ? 1.2 : 1.15, letterSpacing: 0, fontFamily: headingFont(lang),
          textShadow: '0 2px 24px rgba(0,42,23,0.45)', animation: 'sedra-slideIn 0.5s 0.08s ease both',
        }}>
          {ar ? slide.titleAr : slide.titleEn}
        </h1>
        {(ar ? slide.subAr : slide.subEn) ? (
          <p key={`sub-${current}`} style={{ margin: 0, fontSize: isMobile ? 13 : 15, color: BRAND.cream, opacity: 0.92, fontWeight: 500, fontFamily: FONT_BODY, animation: 'sedra-slideIn 0.5s 0.16s ease both' }}>
            {ar ? slide.subAr : slide.subEn}
          </p>
        ) : null}
        {onApply && (
          <button type="button" onClick={onApply}
            style={{
              pointerEvents: 'auto', marginTop: isMobile ? 18 : 24,
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: isMobile ? '12px 26px' : '14px 34px', minHeight: 44,
              borderRadius: RADIUS.pill, background: TOKENS.pistachioDeep, color: BRAND.cream,
              border: 'none', cursor: 'pointer', fontSize: isMobile ? 13 : 14,
              fontWeight: 700, fontFamily: FONT_BODY,
              boxShadow: '0 10px 32px rgba(0,42,23,0.35)',
              transition: 'transform 0.15s, box-shadow 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}>
            <KeyRound size={16} aria-hidden="true" />
            {ar ? 'تقدّم للحصول على أسعار الجملة' : 'Apply for Wholesale Access'}
          </button>
        )}
        {/* Slide dots */}
        {count > 1 && (
          <div style={{ display: 'flex', gap: 6, marginTop: 16, pointerEvents: 'auto' }}>
            {slides.map((_, i) => (
              <button key={i} type="button" onClick={() => setCurrent(i)}
                aria-label={(ar ? 'شريحة ' : 'Slide ') + (i + 1)}
                aria-current={i === current}
                style={{
                  width: i === current ? 18 : 8, height: 8, borderRadius: 4, padding: 0,
                  border: 'none', cursor: 'pointer',
                  background: i === current ? BRAND.tan : 'rgba(255,255,255,0.4)',
                  transition: 'all 0.3s ease',
                }} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
